import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAllTimeEntries, useDeleteTimeEntry } from '@/hooks/use-time-entries';
import { cn } from '@/lib/utils';
import { formatDate, toLocalDayKey } from '@/lib/date';
import type { TimeEntryItem } from '@hygge/shared';
import { ChevronRight, Copy, Pencil, Plus, TriangleAlert, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { groupEntriesByDay, weekDefaultDay, weekFetchWindow } from '../lib/week-days';
import { viewerTimeZone } from '@/lib/timezone';
import { entryClock, entryEndWall, entryStartWall } from '../lib/wall-clock';
import { canDeleteEntry, canEditEntry, entryLockMessage, serverAcceptsCopy } from '../lib/entry-lock';
import { isDayOverfull, OVERFULL_DAY_HINT } from '../lib/day-capacity';
import { ManualEntryForm } from './manual-entry-form';
import { EditEntryDialog } from './edit-entry-dialog';
import { DeleteEntryConfirm } from './delete-entry-confirm';
import { formatEntryDuration } from '../lib/entry-duration';
import { canDuplicateEntry, duplicateSeed } from '../lib/duplicate-entry';

interface WeeklyTimesheetProps {
  weekStart: Date;
}

export function WeeklyTimesheet({ weekStart }: WeeklyTimesheetProps) {
  // A day wider than the week on each side, so a legacy row the viewer's day
  // puts inside this week is fetched at all; see `weekFetchWindow`. Every page
  // is still read (HW-869), the wider window only adds rows. [HW-916]
  const { from, to } = useMemo(() => weekFetchWindow(weekStart), [weekStart]);
  const { data } = useAllTimeEntries({ from, to });
  const deleteMutation = useDeleteTimeEntry();
  const [logDate, setLogDate] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<TimeEntryItem | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<TimeEntryItem | null>(null);
  /** The entry Log Time was opened as a copy of. [HW-905] */
  const [copyingEntry, setCopyingEntry] = useState<TimeEntryItem | null>(null);

  // The viewer's own calendar day. The UTC reading (`toISOString`) named
  // yesterday until 03:00 for a viewer at UTC+3 and tomorrow from 17:00 at
  // UTC-7, putting the "Today" badge on a neighbouring row. [HW-858]
  const todayKey = toLocalDayKey(new Date());

  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => new Set([todayKey]));

  // Reset expanded days when week changes — expand today if in view, otherwise
  // the first day, which is now always the header's Monday: keys are local
  // calendar days, closing the half of HW-834 that stayed open. [HW-858]
  useEffect(() => {
    setExpandedDays(new Set([weekDefaultDay(weekStart, todayKey)]));
  }, [weekStart, todayKey]);

  const toggleDay = useCallback((dateKey: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  }, []);

  // Resolved once per render, then handed to both `groupEntriesByDay` and every
  // row this week prints, rather than asked again for the same render; not
  // memoised, so a laptop that changes zone reads the next render in the new
  // one. [HW-916]
  const viewerZone = viewerTimeZone();

  const entriesByDay = useMemo(
    () => groupEntriesByDay(weekStart, data?.data ?? [], viewerZone),
    [data, weekStart, viewerZone],
  );

  // Log Time opens on today when the viewed week holds it, otherwise on that
  // week's Monday: logging into a past week is the reason you navigated there,
  // so the date should already be inside it rather than today's. Same rule the
  // expanded-day default uses. [HW-868]
  const weekLogDate = useMemo(() => weekDefaultDay(weekStart, todayKey), [weekStart, todayKey]);

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">Weekly Timesheet</CardTitle>
        <Button size="sm" onClick={() => setLogDate(weekLogDate)} className="gap-1.5 h-7 text-xs">
          <Plus className="h-3 w-3" />
          Log Time
        </Button>
      </CardHeader>
      <CardContent className="space-y-0">
        {Array.from(entriesByDay.entries()).map(([dateKey, entries], idx) => {
          const dayTotal = entries.reduce((sum, e) => sum + e.duration, 0);
          const isToday = dateKey === todayKey;
          const isExpanded = expandedDays.has(dateKey);
          const hasEntries = entries.length > 0;

          return (
            <div key={dateKey} className={cn(idx > 0 && 'border-t border-border')}>
              {/* Day header — a toggle and a per-day Log Time affordance, side by
                  side because a button cannot hold a button; the toggle still only
                  expands, the "+" opens Log Time on this day. [HW-868] */}
              <div
                className={cn(
                  'group/day flex items-center rounded-md transition-colors hover:bg-muted/50',
                  isToday && 'bg-hygge/5',
                )}
              >
                <button
                  type="button"
                  onClick={() => toggleDay(dateKey)}
                  className="flex flex-1 items-center justify-between py-3 px-1 text-left cursor-pointer"
                >
                <div className="flex items-center gap-2.5">
                  <ChevronRight
                    className={cn(
                      'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
                      isExpanded && 'rotate-90',
                    )}
                  />
                  {/* Both labels are read off the bucket's own key. The weekday used to come
                      from the loop index, which assumed the first bucket was a Monday and so
                      named every day one weekday ahead of its date (23 Aug 2026, a Sunday, was
                      headed "Monday"). `formatDate` renders a date-only value in UTC, matching
                      the key and every other date-only label in the app — `new Date(key)` plus
                      local formatting was also a day early for viewers west of UTC. HW-834. */}
                  <span className={cn(
                    'text-xs font-medium',
                    isToday ? 'text-hygge' : 'text-foreground',
                  )}>
                    {formatDate(dateKey, { weekday: 'long' })}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {formatDate(dateKey, 'monthDay')}
                  </span>
                  {isToday && (
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-hygge bg-hygge/10 px-1.5 py-0.5 rounded">
                      Today
                    </span>
                  )}
                  {!isExpanded && hasEntries && (
                    <span className="text-[10px] text-muted-foreground/60">
                      {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                    </span>
                  )}
                </div>
                {/* A total that cannot be true is not printed in the same ink as
                    one that can, and not by colour alone — the icon carries it
                    for a reader who cannot tell the two apart. The row and the
                    server's cap now partition by the same day, the author's own
                    [HW-916], so the usual ways a row gets here are two: stored
                    before the API began refusing such days, or written by the
                    timer, which still can [HW-886]. A third lasts only while
                    legacy rows remain unconverted — those are bucketed by this
                    reader's day, which need not be the day the server capped;
                    see `day-capacity.ts`. So this stays a remark about the total
                    on screen, not a copy of the server's rule. Nothing here
                    blocks anything; it says where to look. [HW-859] */}
                <span
                  className={cn(
                    'flex items-center gap-1.5 text-xs font-mono',
                    isDayOverfull(dayTotal)
                      ? 'text-destructive'
                      : dayTotal > 0
                        ? 'text-foreground'
                        : 'text-muted-foreground/30',
                  )}
                  title={isDayOverfull(dayTotal) ? OVERFULL_DAY_HINT : undefined}
                >
                  {isDayOverfull(dayTotal) && (
                    <TriangleAlert className="h-3 w-3 shrink-0" aria-label={OVERFULL_DAY_HINT} />
                  )}
                  {formatEntryDuration(dayTotal)}
                </span>
                </button>
                <button
                  type="button"
                  onClick={() => setLogDate(dateKey)}
                  title="Log time on this day"
                  className="mr-1 h-6 w-6 rounded-md flex items-center justify-center opacity-0 group-hover/day:opacity-100 focus-visible:opacity-100 shrink-0 text-muted-foreground hover:text-hygge hover:bg-hygge/10 transition-all cursor-pointer"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Entries — collapsible */}
              <div
                className={cn(
                  'transition-all duration-200 ease-in-out',
                  // A day is capped at 500px, and the rows inside it now read oldest-first, so
                  // the entry you just logged is the one at the bottom. Measured on a day with
                  // 21 entries: 844px of rows in a 500px box, the newest 336px below the edge
                  // with no way to reach it. Expanded therefore scrolls; collapsed keeps
                  // `overflow-hidden`, which is what makes the max-height animation work.
                  isExpanded
                    ? 'max-h-[500px] overflow-y-auto opacity-100'
                    : 'max-h-0 overflow-hidden opacity-0',
                )}
              >
                {hasEntries ? (
                  <div className="pb-2 pl-6 space-y-1">
                    {entries.map((entry) => {
                      /* Imported hours are the import's, not the timesheet's:
                         the API refuses to edit or delete a row that still has
                         to match the Clockify entry it names, so offering the
                         affordance would only produce an error. Undoing the
                         import run is how these come out.

                         That reasoning is not the import's alone — a month that
                         has ended refuses the same two verbs — so the gate is
                         now the server's own `canEdit`, which folds provenance,
                         the monthly cutoff and ownership into one answer. It
                         also removes a proxy: the check here read `source`,
                         while the API refuses on `externalSource`. [HW-864] */
                      const locked = !canEditEntry(entry);
                      const imported = entry.source === 'clockify' || entry.source === 'tempo' || entry.source === 'paper';
                      /* Withheld on a row that cannot be copied usefully —
                         one running past midnight, or shorter than a minute
                         (`canDuplicateEntry`) [HW-905] — and on one whose copy
                         the server would refuse (`canDuplicate`): a copy is a
                         create on the source's date, and neither "any time
                         log" grant widens a create, so a holder's own
                         closed-month row can open to the pencil and still not
                         take a copy; nor can a task's row on a closed project.
                         Read apart from `locked` for the same reason the trash
                         is. [HW-972] */
                      const duplicateButton =
                        serverAcceptsCopy(entry) && canDuplicateEntry(entry, viewerZone) ? (
                          <button
                            className="h-6 w-6 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 shrink-0 text-muted-foreground hover:text-hygge hover:bg-hygge/10 transition-all cursor-pointer"
                            title="Duplicate entry"
                            onClick={(e) => {
                              // Opens the dialog rather than writing a copy:
                              // a control this small, revealed under a moving
                              // pointer, must not create a real entry on a
                              // misplaced click. [HW-905] [HW-843]
                              e.stopPropagation();
                              setCopyingEntry(entry);
                            }}
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        ) : null;
                      /* The pencil follows `canEdit`; the trash follows
                         `canDelete` — the two grants behind them are
                         independent since HW-972, and both flags fold in the
                         role's route permissions, so a trash the API would
                         refuse is never drawn. Built once,
                         because a locked row may still carry it: a holder of
                         "Delete any time log" alone can remove a row they
                         cannot edit. [HW-972] */
                      const deleteButton = canDeleteEntry(entry) ? (
                        <button
                          /* `focus-visible` alongside the hover reveal: the
                             button now opens a confirm dialog, and Radix hands
                             focus back to it on close — without this a
                             keyboard user lands on a control drawn at zero
                             opacity. [HW-843] */
                          className="h-6 w-6 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all cursor-pointer"
                          title="Delete entry"
                          onClick={(e) => {
                            // The row underneath opens the Edit dialog, so this
                            // click must not reach it — and it asks before
                            // deleting rather than firing the mutation. [HW-843]
                            e.stopPropagation();
                            setDeletingEntry(entry);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      ) : null;
                      return (
                      <div
                        key={entry.id}
                        title={entryLockMessage(entry) ?? undefined}
                        className={cn(
                          'flex items-center justify-between gap-4 px-3 py-1.5 rounded-md group',
                          locked ? 'cursor-default' : 'hover:bg-accent/50 cursor-pointer',
                        )}
                        onClick={() => {
                          if (!locked) setEditingEntry(entry);
                        }}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {entry.task && (
                            <span className="text-[11px] font-mono text-primary shrink-0">{entry.task.key}</span>
                          )}
                          <span className="text-xs text-foreground truncate">
                            {entry.description || entry.task?.title || entry.project.name}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            <div className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: entry.project.color }} />
                            <span className="text-[10px] text-muted-foreground">{entry.project.name}</span>
                          </div>
                        </div>
                        {/* Never squeezed: a long description truncates on the left instead
                            of wrapping the clock onto three lines. */}
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="whitespace-nowrap text-[11px] text-muted-foreground">
                            {/* The author's clock, as typed — the stored UTC digits, not
                                that instant in the viewer's zone. A legacy row the
                                backfill has not converted yet is still a real instant,
                                so it prints on the viewer's clock, as before. [HW-916] */}
                            {entryClock(entryStartWall(entry, viewerZone))}
                            {entry.endedAt && ` — ${entryClock(entryEndWall(entry, viewerZone) ?? '')}`}
                          </span>
                          <span className="text-xs font-mono text-foreground min-w-[48px] text-right">
                            {formatEntryDuration(entry.duration)}
                          </span>
                          {/* The badge belongs to imported rows specifically; a
                              row locked for any other reason offers no edit, and
                              the trash only when the server answered `canDelete`
                              — otherwise the API would refuse it. [HW-972] */}
                          {imported ? (
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">{entry.source}</span>
                          ) : locked ? (
                            <>
                            {duplicateButton}
                            {deleteButton}
                            </>
                          ) : (
                            <>
                            <button
                              className="h-6 w-6 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-all cursor-pointer"
                              title="Edit entry"
                              onClick={(e) => {
                                // The row already opens Edit on click; the pencil is
                                // that same action made visible next to delete. The
                                // row stays clickable as before.
                                e.stopPropagation();
                                setEditingEntry(entry);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            {duplicateButton}
                            {deleteButton}
                            </>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="pb-3 pl-6">
                    <p className="text-[11px] text-muted-foreground/40 italic">No time entries</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>

      {logDate !== null && (
        <ManualEntryForm defaultDate={logDate} onClose={() => setLogDate(null)} />
      )}
      {copyingEntry && (
        <ManualEntryForm
          key={copyingEntry.id}
          {...duplicateSeed(copyingEntry)}
          onClose={() => setCopyingEntry(null)}
        />
      )}
      {editingEntry && <EditEntryDialog entry={editingEntry} onClose={() => setEditingEntry(null)} />}
      <DeleteEntryConfirm
        entry={deletingEntry}
        isPending={deleteMutation.isPending}
        onCancel={() => setDeletingEntry(null)}
        onConfirm={(entry) =>
          // Left open on failure, where the global error toast says why and the
          // Delete button is live again — closing it would report success.
          deleteMutation.mutate(entry.id, { onSuccess: () => setDeletingEntry(null) })
        }
      />
    </Card>
  );
}
